import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLenis } from "lenis/react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import { useSite } from "@/context/SiteContext";
import { trackWaClick } from "@/lib/api";

const LEGACY_LINKS = [
  { label: "Inicio", target: "#inicio" },
  { label: "Descargas", target: "#descargas" },
  { label: "Video", target: "#video" },
  { label: "Contacto", target: "#contacto" },
];

export default function Navbar() {
  const lenis = useLenis();
  const navigate = useNavigate();
  const { content } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.querySelector(id);
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: -72 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const waNumber = (content?.contact?.whatsapp || "3624723836").replace(/\D/g, "");
  const waLink = `https://wa.me/54${waNumber}?text=${encodeURIComponent(content?.contact?.whatsapp_message || "Hola AUREX")}`;
  const nav = content?.nav || {};
  const links = (nav.links && nav.links.length) ? nav.links : LEGACY_LINKS;
  const consultLabel = nav.consult_label || "Consultar";
  const adminLabel = nav.admin_label || "Admin";
  const mobileCta = nav.mobile_cta || "Consultar por WhatsApp";
  const showAdmin = nav.show_admin_button !== false;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "glass border-b border-white/10 py-3" : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 md:px-10">
        <button data-testid="nav-logo" onClick={() => scrollTo("#inicio")} className="shrink-0">
          <Logo size={36} />
        </button>

        <nav className="hidden items-center gap-9 lg:flex">
          {links.map((l) => (
            <button
              key={l.target}
              data-testid={`nav-link-${(l.label || "").toLowerCase()}`}
              onClick={() => scrollTo(l.target)}
              className="group relative font-mono-jb text-xs uppercase tracking-[0.18em] text-[color:var(--aurex-muted)] transition-colors duration-300 hover:text-[color:var(--aurex-text)]"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[color:var(--aurex-accent)] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {showAdmin && (
            <button
              data-testid="nav-admin-btn"
              onClick={() => navigate("/admin")}
              className="font-mono-jb text-xs uppercase tracking-[0.18em] text-[color:var(--aurex-muted)] transition-colors hover:text-[color:var(--aurex-text)]"
            >
              {adminLabel}
            </button>
          )}
          <a
            data-testid="nav-whatsapp-btn"
            href={waLink}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackWaClick("nav")}
            className="group flex items-center gap-2 rounded-full copper-bg px-5 py-2.5 text-sm font-semibold text-[color:var(--aurex-on-accent)] transition-transform duration-300 hover:scale-[1.04]"
          >
            {consultLabel}
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-[color:var(--aurex-text)] lg:hidden"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden glass border-t border-white/10 lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {links.map((l) => (
                <button
                  key={l.target}
                  data-testid={`nav-mobile-link-${(l.label || "").toLowerCase()}`}
                  onClick={() => scrollTo(l.target)}
                  className="py-3 text-left font-display text-2xl font-bold text-[color:var(--aurex-text)]"
                >
                  {l.label}
                </button>
              ))}
              {showAdmin && (
                <button
                  onClick={() => { setOpen(false); navigate("/admin"); }}
                  className="py-3 text-left font-mono-jb text-xs uppercase tracking-[0.2em] text-[color:var(--aurex-muted)]"
                >
                  {adminLabel}
                </button>
              )}
              <a href={waLink} target="_blank" rel="noreferrer" onClick={() => trackWaClick("nav-mobile")} className="mt-2 rounded-full copper-bg py-3 text-center text-sm font-semibold text-[color:var(--aurex-on-accent)]">
                {mobileCta}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
